import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBtn,
  MDBModal,
  MDBModalBody,
  MDBIcon,
  MDBModalHeader,
  MDBInput,
} from "mdbreact";
import {
  toggleModal,
  SAVE,
  UPDATE,
} from "../../../../../services/redux/slices/diagnostics/clinic/clinicMenus";
import { isEqual } from "lodash";
import { useToasts } from "react-toast-notifications";
import {
  getClinicRecordId,
  getMenuPhysicians,
  getPhysicianName,
  getPhysicianRecordId,
} from "./utils";

const measureOptions = ["mcg", "mg", "g", "kg", "ml", "l", "iu", "%"];
const unitOptions = [
  "tablet",
  "capsule",
  "softgel",
  "sachet",
  "ampule",
  "vial",
  "bottle",
  "tube",
  "nebule",
  "inhaler",
  "patch",
  "suppository",
  "drop",
  "spray",
  "pcs",
];

export default function Modal() {
  const { showModal, selected, willCreate, formSubmitted, activeView } =
      useSelector(({ clinicMenus }) => clinicMenus),
    { token, activePlatform } = useSelector(({ auth }) => auth),
    { collections: clinics = [] } = useSelector(({ clinicInfo }) => clinicInfo),
    authId = useSelector(({ auth }) => auth?.auth?._id || ""),
    [form, setForm] = useState({}),
    { addToast } = useToasts(),
    dispatch = useDispatch();

  const physicians = getMenuPhysicians({
    activePlatform,
    clinics,
    authId,
  })
    .map((physician) => {
      const physicianId = getPhysicianRecordId(physician);
      const clinicId = getClinicRecordId(physician);

      return {
        raw: physician,
        physicianId,
        clinicId,
        label: getPhysicianName(physician),
      };
    })
    .filter(({ physicianId }) => Boolean(physicianId));

  // Initialize form when selected changes
  useEffect(() => {
    if (selected) {
      setForm({
        itemType: activeView || "service",
        ...selected,
        physician: selected?.physician?._id || selected?.physician || "",
        clinic: selected?.clinic?._id || selected?.clinic || "",
      });
    } else {
      setForm({});
    }
  }, [selected, activeView]);

  useEffect(() => {
    if (!showModal || !willCreate || !physicians.length) return;

    setForm((prev) => {
      if (prev?.physician && prev?.clinic) return prev;

      const selectedPhysician =
        physicians.find(
          ({ physicianId }) => String(physicianId) === String(prev?.physician),
        ) || physicians[0];

      if (!selectedPhysician) return prev;

      return {
        ...prev,
        physician: prev?.physician || selectedPhysician.physicianId,
        clinic: prev?.clinic || selectedPhysician.clinicId,
      };
    });
  }, [physicians, showModal, willCreate]);

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.physician) {
        return addToast("Please select a tagged physician first.", {
          appearance: "error",
        });
      }

      if (!form.clinic) {
        return addToast(
          "The selected physician does not have a tagged clinic yet.",
          {
            appearance: "error",
          },
        );
      }

      const payload = {
        ...form,
        physician: form?.physician?._id || form?.physician || "",
        clinic: form?.clinic?._id || form?.clinic || "",
        srp: Number(form?.srp || 0),
        strength: form?.itemType === "meds" ? Number(form?.strength || 0) : undefined,
        measure: form?.itemType === "meds" ? form?.measure || "" : undefined,
        unit: form?.itemType === "meds" ? form?.unit || "pcs" : undefined,
      };

      if (willCreate) {
        await dispatch(SAVE({ data: payload, token }));
        setForm({});
      } else {
        if (
          isEqual(payload, {
            ...selected,
            physician: selected?.physician?._id || selected?.physician || "",
            clinic: selected?.clinic?._id || selected?.clinic || "",
            srp: Number(selected?.srp || 0),
          })
        ) {
          return addToast("No changes found, skipping update.", {
            appearance: "info",
          });
        }
        await dispatch(UPDATE({ data: payload, token }));
      }
    } catch (err) {
      addToast(err.message || "Failed to save/update.", {
        appearance: "error",
      });
    }
  };

  // STATE HELPERS
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleValue = (key) => form[key] ?? "";

  const dosagePreview =
    form.itemType === "meds"
      ? form?.strength
        ? `${form.strength} ${form.measure || ""}/${form.unit || "pcs"}`
            .replace(/\s+/g, " ")
            .trim()
        : form?.unit || "pcs"
      : "";

  const handleClose = () => dispatch(toggleModal());

  return (
    <MDBModal isOpen={showModal} toggle={handleClose} backdrop size="sm">
      <MDBModalHeader
        toggle={handleClose}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="clinic-medical" className="mr-2" />
        {willCreate ? "Create" : "Update"} Clinic Menu
      </MDBModalHeader>

      <MDBModalBody className="mb-0">
        <form onSubmit={handleSubmit}>
          <small>Menu Type</small>
          <select
            className="form-control mb-3"
            value={form.itemType || activeView || "service"}
            onChange={(e) => handleChange("itemType", e.target.value)}
          >
            <option value="service">Service</option>
            <option value="meds">Medicine</option>
          </select>

          <MDBInput
            label={
              form.itemType === "meds" ? "Medicine Name" : "Service / Procedure"
            }
            type="text"
            value={handleValue("abbreviation")}
            required
            onChange={(e) => handleChange("abbreviation", e.target.value)}
          />

          <MDBInput
            label="Description"
            type="text"
            value={handleValue("description")}
            required
            onChange={(e) => handleChange("description", e.target.value)}
          />

          <MDBInput
            label="SRP"
            type="number"
            value={handleValue("srp")}
            required
            onChange={(e) => handleChange("srp", e.target.value)}
          />

          {form.itemType === "meds" && (
            <>
              <div className="row">
                <div className="col-md-4">
                  <MDBInput
                    label="Strength"
                    type="number"
                    value={handleValue("strength")}
                    onChange={(e) => handleChange("strength", e.target.value)}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <small>Measure</small>
                  <select
                    className="form-control"
                    value={handleValue("measure")}
                    onChange={(e) => handleChange("measure", e.target.value)}
                  >
                    <option value="">-- Select Measure --</option>
                    {measureOptions.map((measure) => (
                      <option key={measure} value={measure}>
                        {measure}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <small>Unit</small>
                  <select
                    className="form-control"
                    value={handleValue("unit") || "pcs"}
                    onChange={(e) => handleChange("unit", e.target.value)}
                  >
                    {unitOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <small className="text-muted d-block mb-3">
                Dosage: {dosagePreview || "N/A"}
              </small>
              <small className="text-muted d-block mb-3">
                Product stock is now managed in clinic inventory.
              </small>
            </>
          )}

          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="discountable"
              checked={!!form.discountable}
              onChange={(e) => handleChange("discountable", e.target.checked)}
            />
            <label className="form-check-label" htmlFor="discountable">
              Discountable
            </label>
          </div>

          <small>Doctor / Specialist</small>
          <select
            className="form-control mb-3"
            value={form.physician || ""}
            onChange={(e) => {
              const selectedPhysician =
                physicians.find(
                  ({ physicianId }) =>
                    String(physicianId) === String(e.target.value),
                ) || {};

              setForm((prev) => ({
                ...prev,
                physician: selectedPhysician.physicianId || "",
                clinic: selectedPhysician.clinicId || "",
              }));
            }}
          >
            <option value="">-- Select Doctor --</option>
            {physicians.map(({ physicianId, label, clinicId }) => (
              <option key={physicianId} value={physicianId}>
                {label || "Unnamed physician"}
                {!clinicId ? " (No clinic yet)" : ""}
              </option>
            ))}
          </select>
          {!physicians.length && (
            <small className="text-danger d-block mb-3">
              No physician with an active clinic was found for this account yet.
            </small>
          )}

          <div className="text-center mb-1-half">
            <MDBBtn
              type="submit"
              disabled={formSubmitted}
              color="info"
              className="mb-2"
              rounded
            >
              {willCreate ? "Submit" : "Update"}
            </MDBBtn>
          </div>
        </form>
      </MDBModalBody>
    </MDBModal>
  );
}
